/* v8 ignore start */
import {Collection} from "@/lib/core/collection";
import {CollectionGroup} from "@/lib/core/collection/group/collectionGroup";
import {StringUtils} from "@/lib/utils/stringUtils";

type TCollectionSource<T> = BaseCollection<T, any> | T[];

export abstract class BaseCollection<T, TResolved = T> {
  private readonly _items: T[];

  public constructor(source: TCollectionSource<T> = []) {
    this._items = Array.isArray(source) ? source : source._items;
  }

  protected get items(): T[] {
    return this._items;
  }

  // region One

  public get firstOrThrow(): TResolved {
    return this.first || throwError(new Error("Unable to get first item from empty source"));
  }

  public get first(): Maybe<TResolved> {
    if (this._items.length > 0) {
      return this.resolve(this._items[0]);
    }
  }

  public get lastOrThrow(): TResolved {
    return this.last || throwError(new Error("Unable to get last item from empty source"));
  }

  public get last(): Maybe<TResolved> {
    if (this._items.length > 0) {
      return this.resolve(this._items[this._items.length - 1]);
    }
  }

  public find(predicate: (item: T) => boolean): Maybe<TResolved> {
    const item = this._items.find(predicate);

    return item && this.resolve(item);
  }

  public findLast(predicate: (item: T) => boolean): Maybe<TResolved> {
    const item = this._items.slice().reverse().find(predicate);

    return item && this.resolve(item);
  }

  public findOrThrow(predicate: (item: T) => boolean, errorFactory?: () => Error): TResolved {
    return this.find(predicate) || throwError(errorFactory ? errorFactory() : new Error("Unable to find item"));
  }

  public get(index: number): Maybe<TResolved> {
    const item = this._items[index];

    return item && this.resolve(item);
  }

  public getOrThrow(index: number): TResolved {
    return this.get(index) ?? throwError(new Error("Unable to get item by index"));
  }

  // endregion

  // region Many

  public skip(count: number): this {
    return this.slice(count);
  }

  public take(count: number): this {
    return this.slice(0, count);
  }

  public slice(startIndex: number, endIndex?: number): this {
    return this.subset(this._items.slice(startIndex, endIndex));
  }

  public splitIntoChunks(chunkSize: number): Collection<this> {
    const chunks = [];

    for (let size = this._items.length, start = 0; start < size; start += chunkSize) {
      chunks.push(this.slice(start, start + chunkSize));
    }

    return new Collection(chunks);
  }

  public select(predicate: (item: T) => boolean): this {
    return this.subset(this._items.filter(predicate));
  }

  public selectWhen(condition: boolean, predicate: (item: T) => boolean): this {
    if (condition) {
      return this.select(predicate);
    }

    return this;
  }

  public sort(comparator: (a: T, b: T) => number): this {
    return this.subset([...this._items].sort(comparator));
  }

  public sortBy(sortFn: (item: T) => number): this;
  public sortBy(sortFn: (item: T) => string): this;
  public sortBy(sortFn: (item: T) => string | number): this {
    return this.sort((a, b) => {
      const aIndex = sortFn(a);
      const bIndex = sortFn(b);

      if (StringUtils.isString(aIndex)) {
        return aIndex.localeCompare(bIndex as string);
      } else {
        return aIndex - (bIndex as number);
      }
    });
  }

  public reverse(): this {
    return this.subset([...this._items].reverse());
  }

  public concat(array: BaseCollection<T, any>): this {
    return this.subset([...this._items, ...array._items]);
  }

  public update(index: number, value: T): this {
    const nextItems = [...this._items];

    nextItems[index] = value;

    return this.subset(nextItems);
  }

  public prepend(item: T): this {
    return this.subset([item, ...this._items]);
  }

  public append(item: T): this {
    return this.subset([...this._items, item]);
  }

  public remove(index: number): this {
    const nextItems = [...this._items];

    nextItems.splice(index, 1);

    return this.subset(nextItems);
  }

  public distinct<TValue>(predicate: (item: T) => TValue, hashFn?: (value: TValue) => any): Collection<TValue> {
    const set = new Set();
    const result = new Collection<TValue>([]);

    for (const item of this._items) {
      const value = predicate(item);
      const hashKey = hashFn ? hashFn(value) : value;

      if (!set.has(hashKey)) {
        result._items.push(value);
        set.add(hashKey);
      }
    }

    return result;
  }

  public groupBy<TKey>(groupFn: (item: T) => TKey, hashFn?: (key: TKey) => any): CollectionGroup<TKey, this> {
    const map = new Map<any, CollectionGroup.IEntry<TKey, this>>();

    for (const item of this._items) {
      const key = groupFn(item);
      const hashKey = hashFn ? hashFn(key) : key;

      let entry = map.get(hashKey);

      if (!entry) {
        entry = {key, group: this.subset([])};
        map.set(hashKey, entry);
      }

      entry.group._items.push(item);
    }

    return new CollectionGroup(Array.from(map.values()));
  }

  public mapAsCollection<TResult>(mapFn: (item: TResolved) => TResult): Collection<TResult> {
    return new Collection(this.toArray().map(mapFn));
  }

  public map<TResult>(mapFn: (item: TResolved, index: number) => TResult): TResult[] {
    return this.toArray().map(mapFn);
  }

  public reduce<TResult>(initial: TResult, reduceFn: (memo: TResult, item: T) => TResult): TResult {
    return this._items.reduce(reduceFn, initial);
  }

  public flatten<TResult>(flattenFn: (item: T) => TResult[]): TResult[] {
    let result: TResult[] = [];
    this._items.map((item) => {
      result = result.concat(flattenFn(item));
    });

    return result;
  }

  public collect(collectFn: (memo: T[], item: T) => void): this {
    const memo: T[] = [];

    this._items.forEach((item) => collectFn(memo, item));

    return this.subset(memo);
  }

  public get edges(): Maybe<[TResolved, TResolved]> {
    if (this.areAny) {
      return [this.firstOrThrow, this.lastOrThrow];
    }
  }

  public get edgesOrThrow(): [TResolved, TResolved] {
    return this.edges || throwError(new Error("Unable to get edges from empty source"));
  }

  // endregion

  // region Helpers

  public get length(): number {
    return this._items.length;
  }

  public get areMissing(): boolean {
    return this._items.length === 0;
  }

  public get areAny(): boolean {
    return this._items.length > 0;
  }

  public get areMany(): boolean {
    return this._items.length > 1;
  }

  public findIndex(predicate: (item: T) => boolean): number {
    return this._items.findIndex(predicate);
  }

  public isSome(predicate: (item: T) => boolean): boolean {
    return this._items.some(predicate);
  }

  public isEvery(predicate: (item: T) => boolean): boolean {
    return this._items.every(predicate);
  }

  public toArray(): TResolved[] {
    return this._items.map((item) => this.resolve(item)!);
  }

  public toCollection(): Collection<TResolved> {
    return new Collection(this.toArray());
  }

  public toJSON(): TResolved[] {
    return this.toArray();
  }

  // endregion

  // region Implementation

  protected abstract subset(items: T[]): this;

  protected abstract resolve(item: T): TResolved;

  // endregion

  // region Helpers One

  protected findInternal(predicate: (item: T) => boolean): Maybe<T> {
    return this._items.find(predicate);
  }

  // endregion
}

function throwError(error: Error): never {
  throw error;
}

interface ISearchOptions<TItem, TItems> {
  previousResult?: ISearchResult<TItem, TItems>;
  maxCollectedPerCycle?: number;
  maxAnalyzedPerCycle?: number;
  maxCycleTime?: number;
}

interface ISearchResult<TItem, TItems> {
  facets: ISearchResultFacets;
  items: TItems;
  itemsAnalyzed: number;
  continue?(options?: ISearchOptions<TItem, TItems>): ISearchResult<TItem, TItems>;
}

interface ISearchResultFacets {
  [key: string]: ISearchResultFacet;
}

interface ISearchResultFacet {
  title: string | JSX.Element;
  value: string;
  options: ISearchResultFacetOptions;
}

interface ISearchResultFacetOptions {
  [key: string]: ISearchResultFacetOption;
}

interface ISearchResultFacetOption {
  title: string | JSX.Element;
  value: string;
  isChecked: boolean;
  count: number;
}
/* v8 ignore stop */
